import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Wallet } from "lucide-react";
import api, { apiError, rupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";

const empty = {
  user_id: "",
  title: "",
  total_amount: 0,
  monthly_amount: 0,
  remaining_months: 0,
  status: "aktif",
  start_date: new Date().toISOString().slice(0, 10),
};

export default function CicilanTab() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [i, u] = await Promise.all([api.get("/installments"), api.get("/users")]);
      setItems(i.data); setUsers(u.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...empty, user_id: users[0]?.id || "" }); setEditing(null); setOpen(true); };
  const openEdit = (it) => {
    setForm({ user_id: it.user_id, title: it.title, total_amount: it.total_amount || 0,
      monthly_amount: it.monthly_amount, remaining_months: it.remaining_months, status: it.status,
      start_date: it.start_date || it.created_at?.slice(0, 10) || empty.start_date });
    setEditing(it); setOpen(true);
  };

  const save = async () => {
    if (!form.user_id || !form.title) return toast.error("Lengkapi karyawan dan judul cicilan");
    setSaving(true);
    try {
      const payload = { ...form, total_amount: Number(form.total_amount), monthly_amount: Number(form.monthly_amount), remaining_months: Number(form.remaining_months) };
      if (editing) await api.put(`/installments/${editing.id}`, payload);
      else await api.post("/installments", payload);
      toast.success("Cicilan disimpan"); setOpen(false); await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Hapus cicilan "${it.title}"?`)) return;
    try { await api.delete(`/installments/${it.id}`); toast.success("Cicilan dihapus"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-5 fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-[#3368A0]" />
          <h2 className="text-xl font-bold text-slate-800">Cicilan Karyawan</h2>
        </div>
        <Button data-testid="add-cicilan-button" onClick={openAdd} className="bg-[#3368A0] hover:bg-[#284f7a] rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> Tambah Cicilan
        </Button>
      </div>
      <p className="text-sm text-slate-500 -mt-2">Potongan cicilan otomatis dikurangi dari gaji bersih setiap periode selama sisa bulan &gt; 0.</p>

      <div className="bg-white rounded-2xl border border-[#C8DFDB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="cicilan-table">
          <thead className="bg-[#C8DFDB]/40 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-3">Karyawan</th>
              <th className="text-left px-5 py-3">Judul</th>
              <th className="text-right px-5 py-3">Total Pinjaman</th>
              <th className="text-right px-5 py-3">Cicilan / Bulan</th>
              <th className="text-right px-5 py-3">Sisa Bulan</th>
              <th className="text-left px-5 py-3">Mulai</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Belum ada data cicilan.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100">
                <td className="px-5 py-3 font-medium text-slate-700">{it.user_name}</td>
                <td className="px-5 py-3 text-slate-600">{it.title}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-600">{rupiah(it.total_amount)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-rose-600">{rupiah(it.monthly_amount)}</td>
                <td className="px-5 py-3 text-right font-mono">{it.remaining_months} bln</td>
                <td className="px-5 py-3 text-slate-600">{it.start_date || "-"}</td>
                <td className="px-5 py-3"><StatusBadge status={it.status === "aktif" ? "aktif" : "nonaktif"} /></td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(it)} data-testid={`edit-cicilan-${it.id}`} className="text-[#3368A0] hover:text-[#284f7a] p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(it)} data-testid={`delete-cicilan-${it.id}`} className="text-rose-500 hover:text-rose-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Cicilan" : "Tambah Cicilan"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Karyawan</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="cicilan-user-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Judul / Keterangan</Label>
              <Input data-testid="cicilan-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Pinjaman motor" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Total Pinjaman (Rp)</Label>
                <Input type="number" data-testid="cicilan-total-input" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cicilan / Bulan (Rp)</Label>
                <Input type="number" data-testid="cicilan-monthly-input" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tanggal Mulai Cicilan</Label>
              <Input type="date" data-testid="cicilan-start-date-input" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Sisa Cicilan (bulan)</Label>
                <Input type="number" data-testid="cicilan-remaining-input" value={form.remaining_months} onChange={(e) => setForm({ ...form, remaining_months: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="cicilan-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="aktif">Aktif</SelectItem><SelectItem value="lunas">Lunas</SelectItem></SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-cicilan-button" onClick={save} disabled={saving} className="bg-[#3368A0] hover:bg-[#284f7a]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
